// ---------------------------------------------------------------------------
// C59+C60+C61+C62: Knowledge Base — CRUD + crawl + upload + semantic search
// ---------------------------------------------------------------------------
import { useEffect, useMemo, useState } from "react";
import { useDebounce } from "@/hooks/use-debounce";
import {
  useKbArticles,
  createKbArticle,
  updateKbArticle,
  deleteKbArticle,
  startCrawl,
  uploadDocument,
  useCrawlStatus,
  useKbSearch,
  type KbArticle,
} from "@/hooks/use-knowledge-base";
import {
  Search,
  Plus,
  FileText,
  Trash2,
  Edit3,
  X,
  Save,
  Loader2,
  AlertCircle,
  Tag,
  Globe,
  Upload,
  Sparkles,
} from "lucide-react";

export default function KnowledgeBasePage() {
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 300);
  const [page, setPage] = useState(1);
  const { articles, pagination, isLoading, error, refetch } = useKbArticles(debouncedSearch || undefined, page, 12);
  const { results: semanticResults, isSearching } = useKbSearch(debouncedSearch || undefined);
  const [editing, setEditing] = useState<KbArticle | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [crawlUrl, setCrawlUrl] = useState("");
  const [crawlId, setCrawlId] = useState<string | null>(null);
  const [isCrawling, setIsCrawling] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const crawlStatus = useCrawlStatus(crawlId ?? undefined);
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPages = Math.max(1, Math.ceil(Math.max(pagination.total, 1) / pagination.limit));

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const startNew = () => {
    setEditing(null);
    setIsNew(true);
    setTitle("");
    setContent("");
    setCategory("General");
    setTagsInput("");
    setActionError(null);
  };

  const startEdit = (article: KbArticle) => {
    setEditing(article);
    setIsNew(false);
    setTitle(article.title);
    setContent(article.content);
    setCategory(article.category);
    setTagsInput(article.tags.join(", "));
    setActionError(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setActionError(null);
    const tags = tagsInput.split(",").map((tag) => tag.trim()).filter(Boolean);
    try {
      if (isNew) {
        await createKbArticle({ title, content, category, tags });
      } else if (editing) {
        await updateKbArticle(editing.id, { title, content, category, tags });
      }
      setEditing(null);
      setIsNew(false);
      refetch();
    } catch {
      setActionError("Could not save the article right now.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setActionError(null);
    try {
      await deleteKbArticle(id);
      refetch();
    } catch {
      setActionError("Could not delete the article right now.");
    }
  };

  const handleStartCrawl = async () => {
    if (!crawlUrl.trim()) return;
    setActionError(null);
    setIsCrawling(true);
    try {
      const result = await startCrawl({
        url: crawlUrl.trim(),
        maxDepth: 1,
        maxPages: 20,
        excludePatterns: [],
        respectRobotsTxt: true,
      });
      setCrawlId(result.id);
      refetch();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Could not start the crawl.");
    } finally {
      setIsCrawling(false);
    }
  };

  const handleUpload = async (file: File | null) => {
    if (!file) return;
    setActionError(null);
    setIsUploading(true);
    try {
      await uploadDocument(file);
      refetch();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Upload failed.");
    } finally {
      setIsUploading(false);
    }
  };

  const isEditorOpen = isNew || editing !== null;
  const bannerError = error ?? actionError;
  const semanticCards = useMemo(() => semanticResults.slice(0, 3), [semanticResults]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Knowledge Base</h1>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            {pagination.total} articles mounted into the AI runtime
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="btn-press inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)]">
            {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {isUploading ? "Uploading..." : "Upload Document"}
            <input
              type="file"
              className="hidden"
              accept=".txt,.md,.markdown,.pdf"
              onChange={(event) => {
                void handleUpload(event.target.files?.[0] ?? null);
                event.currentTarget.value = "";
              }}
            />
          </label>
          <button
            onClick={startNew}
            className="btn-press inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium text-white"
            style={{ background: "var(--gradient-energy)" }}
          >
            <Plus className="h-4 w-4" /> Add Article
          </button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4 shadow-sm">
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-[var(--color-primary)]" />
            <h2 className="text-sm font-semibold text-[var(--color-foreground)]">Crawl Website</h2>
          </div>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            Pull public website content into the tenant knowledge base so AI answers use real services, pricing, and FAQs.
          </p>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            <input
              type="url"
              value={crawlUrl}
              onChange={(e) => setCrawlUrl(e.target.value)}
              placeholder="https://example.com"
              className="flex-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20"
            />
            <button
              onClick={() => void handleStartCrawl()}
              disabled={isCrawling || !crawlUrl.trim()}
              className="btn-press inline-flex items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              style={{ background: "var(--gradient-energy)" }}
            >
              {isCrawling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />}
              Crawl
            </button>
          </div>
          {crawlStatus && (
            <div className="mt-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/30 px-3 py-2 text-xs text-[var(--color-muted-foreground)]">
              <div className="font-medium text-[var(--color-foreground)]">Latest crawl: {crawlStatus.status}</div>
              <div className="mt-1">Pages found: {crawlStatus.pagesFound} · Articles created: {crawlStatus.articlesCreated}</div>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4 shadow-sm">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[var(--color-primary)]" />
            <h2 className="text-sm font-semibold text-[var(--color-foreground)]">Semantic Search</h2>
          </div>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            Search prefers vector similarity when embeddings are available and falls back to full-text when they are not.
          </p>
          <div className="mt-3 space-y-2">
            {isSearching ? (
              <div className="flex items-center gap-2 text-xs text-[var(--color-muted-foreground)]">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching...
              </div>
            ) : semanticCards.length === 0 ? (
              <p className="text-xs text-[var(--color-muted-foreground)]">No semantic matches yet.</p>
            ) : (
              semanticCards.map((result) => (
                <div key={result.articleId} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2">
                  <div className="text-xs font-medium text-[var(--color-foreground)]">{result.title}</div>
                  <div className="mt-1 text-[11px] text-[var(--color-muted-foreground)]">{result.snippet}</div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--color-muted-foreground)]" />
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search articles..."
          className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] py-2.5 pl-10 pr-4 text-sm text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20"
        />
      </div>

      {bannerError && (
        <div className="flex items-center gap-2 rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-3 text-sm text-[var(--color-destructive)]">
          <AlertCircle className="h-4 w-4" />
          {bannerError}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--color-primary)]" />
        </div>
      ) : articles.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-[var(--color-border)] p-12 text-center">
          <FileText className="mx-auto h-10 w-10 text-[var(--color-muted-foreground)]/30" />
          <p className="mt-3 text-sm text-[var(--color-muted-foreground)]">
            No articles yet. Add your first knowledge base article.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="space-y-2">
            {articles.map((article) => (
              <div
                key={article.id}
                className="card-hover flex items-center gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-3 shadow-sm"
              >
                <FileText className="h-5 w-5 flex-shrink-0 text-[var(--color-muted-foreground)]" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-[var(--color-foreground)]">{article.title}</p>
                  <div className="flex items-center gap-2 text-[10px] text-[var(--color-muted-foreground)]">
                    <span>{article.category}</span>
                    {article.tags.slice(0, 3).map((tag) => (
                      <span key={tag} className="flex items-center gap-0.5">
                        <Tag className="h-2.5 w-2.5" />
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="row-actions flex items-center gap-1">
                  <button
                    onClick={() => startEdit(article)}
                    className="rounded-md p-1.5 text-[var(--color-muted-foreground)] transition-colors hover:bg-[var(--color-muted)] hover:text-[var(--color-foreground)]"
                  >
                    <Edit3 className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => void handleDelete(article.id)}
                    className="rounded-md p-1.5 text-[var(--color-muted-foreground)] transition-colors hover:bg-[var(--color-destructive)]/10 hover:text-[var(--color-destructive)]"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2 text-xs text-[var(--color-muted-foreground)] sm:flex-row sm:items-center sm:justify-between">
            <div>
              Showing {Math.min(pagination.offset + 1, pagination.total)}-{Math.min(pagination.offset + articles.length, pagination.total)} of {pagination.total}
            </div>
            <div className="flex items-center gap-2">
              <span>Page {currentPage} of {totalPages}</span>
              <button
                type="button"
                onClick={() => setPage((value) => Math.max(1, value - 1))}
                disabled={currentPage <= 1}
                className="rounded-md border border-[var(--color-border)] px-2 py-1 text-[var(--color-foreground)] disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((value) => value + 1)}
                disabled={!pagination.hasMore}
                className="rounded-md border border-[var(--color-border)] px-2 py-1 text-[var(--color-foreground)] disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {isEditorOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[4px]"
            onClick={() => {
              setEditing(null);
              setIsNew(false);
            }}
          />
          <div className="fixed inset-y-0 right-0 z-50 flex w-[500px] max-w-full flex-col border-l border-[var(--color-border)] bg-[var(--color-card)] shadow-xl animate-in slide-in-from-right duration-300">
            <div className="flex items-center justify-between border-b border-[var(--color-border)] px-6 py-4">
              <h3 className="text-sm font-semibold text-[var(--color-foreground)]">{isNew ? "New Article" : "Edit Article"}</h3>
              <button
                onClick={() => {
                  setEditing(null);
                  setIsNew(false);
                }}
                className="rounded-md p-1 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 space-y-4 overflow-y-auto p-6">
              <div>
                <label className="mb-1 block text-xs font-medium text-[var(--color-muted-foreground)]">Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-[var(--color-muted-foreground)]">Content</label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={12}
                  className="w-full resize-none rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-[var(--color-muted-foreground)]">Category</label>
                <input
                  type="text"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-[var(--color-muted-foreground)]">Tags (comma-separated)</label>
                <input
                  type="text"
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t border-[var(--color-border)] px-6 py-4">
              <button
                onClick={() => {
                  setEditing(null);
                  setIsNew(false);
                }}
                className="rounded-lg px-4 py-2 text-sm text-[var(--color-muted-foreground)]"
              >
                Cancel
              </button>
              <button
                onClick={() => void handleSave()}
                disabled={isSaving || !title.trim()}
                className="btn-press rounded-lg px-5 py-2 text-sm font-medium text-white disabled:opacity-50"
                style={{ background: "var(--gradient-energy)" }}
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 inline h-4 w-4" />Save</>}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
